import React, { useEffect, useState } from "react";
import { Menu, Box } from "@mui/material";
import WorkbookItemCard from "../../shared/cards/WorkbookItemCard";
import { ReportOptionControls, useVisibleReportOptions } from "./ReportOptionControls";
import { useBanner } from "../../context/BannerContext";
import { useDialog } from "../../context/DialogContext";
import { useLoading } from "../../context/LoadingContext";
import { useReportData } from "../../context/ReportDataContext";
import { useReportActions } from "../../hooks/useReportActions";
import { reportTypeOfTemplate } from "../../hooks/useReportCards";
import { DialogDescription, DialogDescriptionWithAlertList } from "../../shared/dialog/Dialog";
import { setCalculationAutomatic, setCalculationManual } from "../../../excel/workbook";
import { cacheableData } from "../../../excel/workbook/cache";
import {
  getReportData,
  getSheetsToDeleteForReportType,
  hasMultiSheetRunner,
  hasSingleSheetRunner,
  ReportTemplate,
} from "../../../excel/reports/api";
import { DatasourceGoneError } from "../../../errors";
import {
  EMPTY_SHEETS_DELETE_DESCRIPTION,
  REPORT_DATA_REFRESHED,
  REPORT_DELETE_NOTICE,
  REPORT_REFRESH_PENDING,
  deleteSheetsTitle,
  reportGenerated,
  reportOptionUpdateError,
  reportRemoveError,
  reportRemoved,
  worksheetNotFound,
} from "../../constants";
import {
  activateReportAndReadSubheader,
  deleteReportSheets,
  namedRangesOnWorksheet,
  readMultiSheetSubheader,
  runReportOnWorksheet,
} from "../../../excel/reports/api/operations";

interface GeneratedReportCardProps {
  template: ReportTemplate;
  onReload: () => Promise<void>;
}

const GeneratedReportCard = ({ template, onReload }: GeneratedReportCardProps) => {
  const { worksheetName, reportType, reportDataSource, splitByKey } = template;
  const { showBanner } = useBanner();
  const { showDialog, hideDialog } = useDialog();
  const { withLoading } = useLoading();
  const { reportDataStatus, refreshReportDataStatusFor } = useReportData();
  const { withReportLogging, changeReportOption, checkReportDataStaleness, refreshReportData } = useReportActions();

  const [options, setOptions] = useState<cacheableData>(template.options);
  const [subtext, setSubtext] = useState<string>(reportType.name);
  /** Non-null while a refresh is in flight; the string names the phase the user is waiting on. */
  const [progress, setProgress] = useState<string | null>(null);
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);
  const [presentNamedRanges, setPresentNamedRanges] = useState<Set<string> | null>(null);
  const loading = progress !== null;

  const isMultiSheet = hasMultiSheetRunner(template);
  const hasData = !!reportDataStatus[worksheetName];
  const visibleDefinitions = useVisibleReportOptions(reportType.optionDefinitions);
  const optionsOpen = !!optionsAnchor;

  // Hiding line item details turns the report into a schedule of values — say so, since the two
  // variants are otherwise indistinguishable in the card.
  const displaySubtext = !hasData ? "No data available" : options.hideLineItemDetails ? `${subtext} SOV` : subtext;

  // Rediscovery hands back a fresh template; without this the card would keep showing the options
  // it mounted with after a regeneration changed them.
  useEffect(() => setOptions(template.options), [template.options]);

  // Checked each time the menu opens: the sheet's named ranges can change between openings.
  useEffect(() => {
    if (!optionsOpen) return;
    const required = visibleDefinitions.flatMap((def) => def.requiredNamedRanges ?? []);
    if (required.length === 0) return;
    let isCurrent = true;
    void namedRangesOnWorksheet(worksheetName, required).then((present) => {
      if (isCurrent) setPresentNamedRanges(present);
    });
    return () => {
      isCurrent = false;
    };
  }, [optionsOpen, visibleDefinitions, worksheetName]);

  useEffect(() => {
    if (!isMultiSheet || !splitByKey) return;
    let isCurrent = true;
    void readMultiSheetSubheader(splitByKey, reportType.name)
      .then((next) => {
        if (isCurrent) setSubtext(next);
      })
      .catch(() => {});
    return () => {
      isCurrent = false;
    };
  }, [isMultiSheet, splitByKey, reportType.name]);

  const runWithLogging = async (callback: () => Promise<void>) => {
    try {
      await withReportLogging(callback, {
        context: "GeneratedReportCard_refresh",
        reportType: reportType.name,
        message: `Failed to generate report for ${worksheetName}`,
      });
    } catch (error) {
      // A gone datasource carries its own prose; the generic message would blame the report engine.
      const message =
        error instanceof DatasourceGoneError ? error.message : `Failed to generate report for ${worksheetName}`;
      showBanner(message, "error");
    }
  };

  const refreshStaleData = async () => {
    // Scoped to this report's own datasource: a detailed estimate report answers for the estimate,
    // not for GCGR or contracting.
    const staleness = await checkReportDataStaleness(reportTypeOfTemplate(template), reportType.staleChecks);
    if (!staleness?.needsRefresh) return;

    const detectedChanges =
      staleness.needsEstimateRefresh || staleness.needsGCGRRefresh || staleness.needsContractingRefresh;

    setProgress("Refreshing data…");
    try {
      await withLoading(async () => {
        if (detectedChanges) showBanner(REPORT_REFRESH_PENDING, "info");
        await refreshReportData(staleness);
        await refreshReportDataStatusFor([template]);
        if (detectedChanges) showBanner(REPORT_DATA_REFRESHED, "success");
      });
    } finally {
      setProgress(null);
    }
  };

  const runReport = async () => {
    try {
      await setCalculationManual();
      setProgress("Refreshing report…");

      if (hasMultiSheetRunner(template)) {
        await template.reportRunner();
        setSubtext(await activateReportAndReadSubheader(reportType.name, template.splitByKey));
        showBanner(reportGenerated(worksheetName), "success");
        return;
      }

      if (!hasSingleSheetRunner(template)) return;

      const ran = await runReportOnWorksheet(worksheetName, template.reportRunner);
      showBanner(ran ? reportGenerated(worksheetName) : worksheetNotFound(worksheetName), ran ? "success" : "error");
    } finally {
      setProgress(null);
      await setCalculationAutomatic();
    }
  };

  /** Multi-sheet regeneration drops split sheets whose group no longer has data — warn first. */
  const confirmOrphanedSheets = async (): Promise<void> => {
    if (!hasMultiSheetRunner(template)) return;
    const data = await getReportData(reportDataSource);
    const sheetsToBeDeleted = await getSheetsToDeleteForReportType(reportType.name, data, template.splitByKey);
    if (!sheetsToBeDeleted.length) {
      await runReport();
      return;
    }

    showDialog({
      title: deleteSheetsTitle(sheetsToBeDeleted.length),
      description: DialogDescription(EMPTY_SHEETS_DELETE_DESCRIPTION),
      alert: {
        content: DialogDescriptionWithAlertList(
          "This action cannot be undone",
          sheetsToBeDeleted.map((sheet) => sheet.name)
        ),
      },
      submit: {
        destructive: true,
        action: async () => {
          hideDialog();
          await runWithLogging(runReport);
        },
      },
      cancel: { action: hideDialog },
    });
  };

  const handleRefresh = async () => {
    if (loading) return;
    await runWithLogging(async () => {
      await refreshStaleData();
      await (isMultiSheet ? confirmOrphanedSheets() : runReport());
    });
  };

  const handleOptionChange = (key: string) => async (value: boolean | string | string[]) => {
    try {
      await changeReportOption(worksheetName, key, value, isMultiSheet);
      setOptions((prev) => ({ ...prev, [key]: value }));
    } catch {
      showBanner(reportOptionUpdateError(key), "error");
    }
  };

  const handleRemove = async () => {
    try {
      await deleteReportSheets(reportType.name, worksheetName);
      showBanner(reportRemoved(worksheetName), "success");
    } catch {
      showBanner(reportRemoveError(worksheetName), "error");
    }
    await onReload();
  };

  return (
    <>
      <WorkbookItemCard
        header={{ text: worksheetName, name: worksheetName, subtext: displaySubtext, subtextIsStatus: !hasData }}
        itemNoun="report"
        action={{ onClick: () => void handleRefresh(), isVisible: hasData }}
        edit={{
          onClick: (event) => setOptionsAnchor(event.currentTarget),
          isVisible: hasData && visibleDefinitions.length > 0,
        }}
        remove={{
          onClick: () => void handleRemove(),
          notice: REPORT_DELETE_NOTICE,
        }}
        loading={loading}
        loadingText={progress ?? undefined}
      />
      <Menu
        anchorEl={optionsAnchor}
        open={!!optionsAnchor}
        onClose={() => setOptionsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", minWidth: "220px" }}>
          <ReportOptionControls
            definitions={visibleDefinitions}
            options={options}
            onOptionChange={handleOptionChange}
            onDropdownCommit={handleRefresh}
            active={!!optionsAnchor}
            loading={loading}
            presentNamedRanges={presentNamedRanges}
          />
        </Box>
      </Menu>
    </>
  );
};

export default GeneratedReportCard;
